import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { insights } from '@repletoryreach/sdk-api';
import { Button, Chip, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, Select, Switch, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { formatDateTime } from '../../lib/format';
import { CHANNELS, INSIGHTS_KEY, focusProblem, insightErrorText, parseFocus } from './lib/data';
import { MUTED, Panel, channelLabel, depthLabel } from './shared';

/**
 * The monthly report (off by default): on the 1st, a report on the month before, with the depth, channels and
 * questions saved here. The same chats-per-report cap bounds every report, monthly or not.
 */
export function SettingsCard() {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [INSIGHTS_KEY, 'settings'], queryFn: () => insights.getInsightSettings() });
  const saved = q.data?.data;
  const [seeded, setSeeded] = useState<insights.InsightSettings | undefined>(undefined);
  const [monthly, setMonthly] = useState(false);
  const [depth, setDepth] = useState<insights.InsightDepth>('standard');
  const [channels, setChannels] = useState<string[]>([]);
  const [focusText, setFocusText] = useState('');
  const [max, setMax] = useState<number | ''>(500);
  const [language, setLanguage] = useState<'en' | 'af'>('en');
  if (saved && saved !== seeded) {
    setSeeded(saved);
    setMonthly(saved.monthly);
    setDepth(saved.depth);
    setChannels(saved.channels);
    setFocusText(saved.focus.join('\n'));
    setMax(saved.max_chats);
    setLanguage(saved.language === 'af' ? 'af' : 'en');
  }
  const focus = parseFocus(focusText);
  const focusError = focusProblem(focus);
  const maxError = max === '' || max < 50 || max > 2000 ? t('insights.settings.maxRange') : null;

  const save = useMutation({
    mutationFn: () => insights.saveInsightSettings({ monthly, depth, channels, focus, max_chats: Number(max), language }),
    onSuccess: (res) => { qc.setQueryData([INSIGHTS_KEY, 'settings'], res); notifySuccess(t('insights.settings.saved'), t('insights.settings.title')); },
    onError: (e) => notifyError(insightErrorText(t, e), t('insights.settings.title'))
  });

  return (
    <Panel title={t('insights.settings.title')} note={t('insights.settings.note')} guide="insights-settings" testId="insights-settings">
      {q.isPending ? <Skeleton height={160} /> : q.error ? <Alert color="error">{insightErrorText(t, q.error)}</Alert> : (
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <Stack gap="md">
            <Switch label={t('insights.settings.monthly')} description={t('insights.settings.monthlyHint')} checked={monthly}
              onChange={setMonthly} data-testid="insights-monthly" />
            {saved?.monthly && saved.next_run_at && (
              <Text role="caption" c={MUTED}>{t('insights.settings.next', { when: formatDateTime(saved.next_run_at) })}</Text>
            )}
            <Select label={t('insights.settings.depth')} description={t('insights.settings.depthHint')} value={depth}
              onChange={(e) => setDepth(e.currentTarget.value as insights.InsightDepth)}
              data={insights.INSIGHT_DEPTHS.map((d) => ({ value: d, label: depthLabel(t, d) }))} />
            <Select label={t('insights.settings.language')} description={t('insights.settings.languageHint')} value={language}
              onChange={(e) => setLanguage(e.currentTarget.value === 'af' ? 'af' : 'en')} data-testid="insights-language"
              data={[{ value: 'en', label: t('insights.settings.languageEn') }, { value: 'af', label: t('insights.settings.languageAf') }]} />
            <Stack gap="xs">
              <Text role="label">{t('insights.new.channels.label')}</Text>
              <Group gap="xs" wrap="wrap" role="group" aria-label={t('insights.new.channels.label')}>
                {CHANNELS.map((c) => (
                  <Chip key={c} size="sm" checked={channels.includes(c)}
                    onChange={(on) => setChannels((cur) => (on ? [...cur, c] : cur.filter((x) => x !== c)))}>
                    {channelLabel(t, c)}
                  </Chip>
                ))}
              </Group>
              <Text role="caption" c={MUTED}>{t('insights.new.channels.hint')}</Text>
            </Stack>
            <Textarea label={t('insights.new.focus.label')} description={t('insights.new.focus.hint')} value={focusText} minRows={2} autosize
              onChange={(e) => setFocusText(e.currentTarget.value)} error={focusError ? t(focusError) : undefined} />
            <NumberInput label={t('insights.settings.max')} description={t('insights.settings.maxHint')} value={max} min={50} max={2000} step={50}
              onChange={setMax} error={maxError ?? undefined} data-testid="insights-max" />
            <Group justify="flex-end">
              <Button type="submit" loading={save.isPending} disabled={!!focusError || !!maxError}>{t('action.saveChanges')}</Button>
            </Group>
          </Stack>
        </form>
      )}
    </Panel>
  );
}
